import { defineStore } from 'pinia';
import { store } from '@/app/stores';
import { Student } from '@/app/api/models/student';
import { Teacher } from '@/app/api/models/teacher';
import { Classroom } from '@/app/api/models/classroom';
import { BaseModuleState } from '@/app/services/modules/BaseModuleService';
import { Module } from '@/app/api/models/module';
import httpClient from '@/app/utils/axios';
import {
    getClassroomSession,
    putSessionToggleFocusScreen,
    startSession,
    doneSession,
    getSessionModules,
} from '@/app/api/session';
import { Session } from '@/app/api/models/session';
import { SessionModule } from '@/app/api/models/sessionModule';
import { getSessionModuleResult, getSessionModuleStudentsResult, storeSessionModule } from '@/app/api/sessionModule';
import { goClassroomInPlay, goTeacherSessionResult } from '@/app/helpers/routerHelper';
import { LessonBlock } from '@/app/api/models/lessonBlock';
import { Curriculum } from '@/app/api/models/curriculum';
import { SessionModeEnum } from '@/enums/SessionModeEnum';

interface ClassroomState {
    isOngoing: boolean;
    classroom: Classroom | null;
    teacher: Teacher | null;
    students: Student[];
    moduleState: BaseModuleState | null;
    studentStates: {
        [studentId: number]: any;
    };
    module: Module | null;
    curriculum: Curriculum | null;
    time: string;
    session: Session | null;
    sessionStudents: {
        [studentId: number]: string;
    };
    sessionTitle: string;
    sessionLessonBlocks: LessonBlock[];
    sessionModule: SessionModule | null;
    sessionModules: SessionModule[];
    isSessionResultUpdate: boolean;
    pendingResultRequestsBySessionModuleId: number[];
    studentResults: {
        [studentId: number]: any;
    };
    isShowStarLayer: boolean; // 칭찬별 레이어 표시 여부
    isFocusScreen: boolean; // 집중 화면 여부
    starsCount: number; // 별 갯수
    // 자습/과제에서 학생 본인이 완료한 sessionModule id 집합.
    // SessionProgressHeader 진행도가 stale session 때문에 즉시 반영되지 않는 문제를 보완하기 위해 사용.
    locallyCompletedSessionModuleIds: number[];
}

export const useClassroomStore = defineStore('app-classroom', {
    state: (): ClassroomState => ({
        isOngoing: false,
        classroom: null,
        teacher: null,
        students: [],
        moduleState: null,
        studentStates: {},
        module: null,
        curriculum: null,
        time: '',
        session: null,
        sessionTitle: '',
        sessionLessonBlocks: [],
        sessionStudents: {},
        sessionModule: null,
        sessionModules: [],
        studentResults: {},
        isSessionResultUpdate: false,
        pendingResultRequestsBySessionModuleId: [], // 결과 요청 대기 목록
        isShowStarLayer: false,
        isFocusScreen: false,
        starsCount: 0,
        locallyCompletedSessionModuleIds: [],
    }),
    getters: {
        getClassroom(): Classroom | null {
            return this.classroom;
        },
        getIsOngoing(): boolean {
            return this.isOngoing;
        },
        getStudents(): Student[] {
            return this.students;
        },
        getTeacher(): Teacher | null {
            return this.teacher;
        },
        getCurriculum(): Curriculum | null {
            return this.curriculum;
        },
        getTime(): string {
            return this.time;
        },
        getModuleState(): BaseModuleState | null {
            return this.moduleState;
        },
        getStudentStates(): { [studentId: number]: any } {
            return this.studentStates;
        },
        getModule(): Module | null {
            return this.module;
        },
        getIsSessionResultUpdate(): boolean {
            return this.isSessionResultUpdate;
        },
        getSession(): Session | null {
            return this.session;
        },
        getSessionTitle(): string {
            return this.sessionTitle;
        },
        getSessionLessonBlocks(): LessonBlock[] {
            return this.sessionLessonBlocks;
        },
        getSessionStudents(): { [studentId: number]: string } {
            return this.sessionStudents;
        },
        getSessionModule(): SessionModule | null {
            return this.sessionModule;
        },
        getSessionModules(): SessionModule[] {
            return this.sessionModules;
        },
        getStudentResults(): { [studentId: number]: any } {
            return this.studentResults;
        },
        getPendingResultRequestsBySessionModuleId(): number[] {
            return this.pendingResultRequestsBySessionModuleId;
        },
        getIsShowStarLayer(): boolean {
            return this.isShowStarLayer;
        },
        getIsFocusScreen(): boolean {
            return this.isFocusScreen;
        },
        getStarsCount(): number {
            return this.starsCount;
        },
    },
    actions: {
        setIsOngoing(isOngoing: boolean) {
            this.isOngoing = isOngoing;
        },
        setClassroom(classroom: Classroom | null) {
            this.classroom = classroom;
        },
        setCurriculum(curriculum: Curriculum | null) {
            this.curriculum = curriculum;
        },
        setTime(time: string) {
            this.time = time;
        },
        setStudents(students: Student[]) {
            this.students = students;
        },
        addStudent(student: Student) {
            this.students = [...this.students, student].sort((a, b) => a.id - b.id);
        },
        updateStudent(studentId: number, student: Partial<Student>) {
            this.students = this.students.map((s) => (s.id === studentId ? { ...s, ...student } : s));
        },
        removeStudent(studentId: number) {
            this.students = this.students.filter((s) => s.id !== studentId);
        },
        setTeacher(teacher: Teacher | null) {
            this.teacher = teacher;
        },
        setModuleState(moduleState: BaseModuleState | null) {
            this.moduleState = moduleState;
        },
        setStudentStates(studentStates: { [studentId: number]: unknown }) {
            this.studentStates = studentStates;
        },
        setStudentState(studentId: number, state: unknown) {
            this.studentStates[studentId] = state;
        },
        setModule(module: Module | null) {
            this.module = module;
        },
        setIsSessionResultUpdate(isSessionResultUpdate: boolean) {
            this.isSessionResultUpdate = isSessionResultUpdate;
        },
        setSession(session: Session | null) {
            // 다른 세션으로 전환되면 직전 세션의 로컬 완료 마킹을 비운다.
            // (Pinia singleton이라 페이지 이동/재진입 시에도 stale id가 남아 진행도가 4/2 같이 계산되는 문제 방지)
            if (this.session?.id !== session?.id) {
                this.locallyCompletedSessionModuleIds = [];
            }
            this.session = session;
        },
        setSessionTitle(sessionTitle: string) {
            this.sessionTitle = sessionTitle;
        },
        setSessionLessonBlocks(sessionLessonBlocks: LessonBlock[]) {
            this.sessionLessonBlocks = sessionLessonBlocks;
        },
        setSessionStudents(sessionStudents: { [studentId: number]: string }) {
            this.sessionStudents = sessionStudents;
        },
        setSessionStudent(studentId: number, name: string) {
            this.sessionStudents[studentId] = name;
        },
        setSessionModule(sessionModule: SessionModule | null) {
            this.sessionModule = sessionModule;
        },
        setStudentResults(studentResults: { [studentId: number]: any }) {
            this.studentResults = studentResults;
        },
        setStudentResult(studentId: number, result: any) {
            this.studentResults[studentId] = {
                ...this.studentResults[studentId],
                ...result,
            };
        },
        setSessionModules(sessionModules: SessionModule[]) {
            this.sessionModules = sessionModules;
        },
        /**
         * 자습/과제에서 학생 본인이 완료한 sessionModule을 로컬에 마킹한다.
         * SessionProgressHeader가 stale session을 union 카운트할 때 사용.
         */
        markSessionModuleLocallyCompleted(sessionModuleId: number) {
            if (this.locallyCompletedSessionModuleIds.includes(sessionModuleId)) return;
            this.locallyCompletedSessionModuleIds.push(sessionModuleId);
        },
        setPendingResultRequestsBySessionModuleId(sessionModuleId: number) {
            this.pendingResultRequestsBySessionModuleId = [
                ...this.pendingResultRequestsBySessionModuleId,
                sessionModuleId,
            ];
        },
        clearPendingResultRequests() {
            this.pendingResultRequestsBySessionModuleId = [];
        },
        setIsFocusScreen(isFocusScreen: boolean) {
            this.isFocusScreen = isFocusScreen;
        },
        setIsShowStarLayer(isShowStarLayer: boolean) {
            this.isShowStarLayer = isShowStarLayer;
        },
        setStarsCount(starsCount: number) {
            this.starsCount = starsCount;
        },
        /**
         * 수업 시작
         */
        async startSession(sessionMode: SessionModeEnum = SessionModeEnum.LECTURE) {
            if (!this.classroom) return;

            const { data } = await startSession(this.classroom, sessionMode);
            this.setSession(data.data.session);

            // 접속자 수업 참여
            const students: { [studentId: number]: string } = {};
            this.students.forEach((student) => {
                students[student.id] = student.name;
            });
            this.setSessionStudents(students);
        },

        /**
         * 수업 완료
         */
        async doneSession() {
            if (!this.classroom || !this.session) return;

            // TASK 모드는 teacher task-history의 마감 엔드포인트를 사용
            // 컨트롤러가 teacher.task-history.index로 redirect하므로 location.href 로 이동시킨다.
            if (this.session.mode === SessionModeEnum.TASK) {
                await httpClient.post(route('teacher.task-history.complete', this.session.id));
                window.location.href = route('teacher.task-history.index');
                return;
            }

            await doneSession(this.classroom);
            goTeacherSessionResult(this.classroom, this.session);
        },

        async getAnotherStudentResult(myStudentId: number) {
            if (!this.sessionModule) return;

            const { data } = await getSessionModuleStudentsResult(this.sessionModule);

            const currentStudents = this.studentResults;
            const newStudents = data.data.students;

            const updatedStudents = {
                ...currentStudents,
                ...Object.fromEntries(
                    Object.entries(newStudents).filter(([studentId]) => Number(studentId) !== myStudentId),
                ),
            };

            this.setStudentResults(updatedStudents);
        },

        /**
         * 활동 결과 조회
         */
        async setSessionModuleResult(isAllStudents = false) {
            if (!this.sessionModule) return;

            const { data } = await getSessionModuleResult(this.sessionModule, isAllStudents);
            this.setSessionModule(data.data.session_module);
            this.setModule(data.data.module);
            this.setStudentResults(data.data.students);
        },

        /**
         * 활동 결과 학생들 결과만 조회
         */
        async setSessionModuleStudentsResult() {
            if (this.session?.mode === SessionModeEnum.SELF || this.session?.mode === SessionModeEnum.TASK) {
                const { data } = await getSessionModules(this.session);
                this.setSessionModules(data.data.session_modules);
            } else {
                if (!this.sessionModule) return;

                const { data } = await getSessionModuleStudentsResult(this.sessionModule);
                this.setStudentResults(data.data.students);
                this.setStudentStates(data.data.student_states);
            }
        },

        /**
         * 활동 진행 중 수업 정보 조회
         */
        async setActiveSessionData() {
            if (!this.classroom) return;

            const { data: classroomSessionData } = await getClassroomSession(this.classroom);
            this.setSession(classroomSessionData.data.session);
            this.setSessionStudents(classroomSessionData.data.session_students);
            this.setStudentStates(classroomSessionData.data.student_states);
            this.setModule(classroomSessionData.data.module);
        },

        /**
         * 활동 시작
         */
        async startModule(module: Module, params: Record<string, unknown>) {
            try {
                if (!this.classroom) return;
                if (isStartModule) return;
                isStartModule = true;

                await storeSessionModule(
                    this.classroom,
                    module,
                    {
                        ...params,
                    },
                    this.getCurriculum?.id,
                );

                // 활동 시작 후 활동 페이지로 이동
                goClassroomInPlay(this.classroom);
                isStartModule = false;
            } catch (error) {
                isStartModule = false;
            }
        },

        /**
         * 집중 화면 on/off
         */
        async toggleFocusScreen() {
            try {
                if (!this.classroom) return;

                const { data } = await putSessionToggleFocusScreen(this.classroom);
                this.setIsFocusScreen(data.data.is_focus_screen);
            } catch (error) {
                console.error(error);
            }
        },
    },
});

let isStartModule = false;

export function useClassroomStoreWithOut() {
    return useClassroomStore(store);
}
